import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { detailedReviews } from '../data/ReviewsData';

const TestimonialCarousel: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % detailedReviews.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const next = () => setCurrentIndex((prev) => (prev + 1) % detailedReviews.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + detailedReviews.length) % detailedReviews.length);

  const review = detailedReviews[currentIndex];
  const starCount = Math.round((review.rating / review.ratingOutOf) * 10); // normalized to 10

  return (
    <div className="relative max-w-4xl mx-auto">
      <AnimatePresence mode="wait">
        <motion.div
          key={review.id}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-lg p-6 md:p-8"
        >
          <div className="flex items-center mb-4">
            {review.avatar ? (
              <img
                src={review.avatar}
                alt={review.name}
                className="w-16 h-16 rounded-full object-cover mr-4"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-earth-brown flex items-center justify-center text-white text-xl font-bold mr-4">
                {review.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-earth-brown">{review.name}</h3>
              <p className="text-gray-600 text-sm">{review.location}</p>
              <div className="flex items-center mt-1">
                {Array.from({ length: 10 }, (_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < starCount ? 'fill-earth-gold text-earth-gold' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          <blockquote className="text-gray-700 text-lg leading-relaxed italic line-clamp-2">
            "{review.comment}"
          </blockquote>

          <div className="mt-2 text-right">
            <Link
              to="/reviews"
              className="inline-flex items-center text-earth-brown font-semibold hover:underline"
            >
              Read full review <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <button
        onClick={prev}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white shadow-lg rounded-full p-2 hover:bg-earth-beige transition-colors"
      >
        <ChevronLeft className="w-6 h-6 text-earth-brown" />
      </button>
      <button
        onClick={next}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white shadow-lg rounded-full p-2 hover:bg-earth-beige transition-colors"
      >
        <ChevronRight className="w-6 h-6 text-earth-brown" />
      </button>

      {/* Dots */}
      <div className="flex justify-center mt-4 space-x-2">
        {detailedReviews.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-colors ${
              index === currentIndex ? 'bg-earth-brown' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialCarousel;
